import { useTranslation } from "react-i18next";
import { useSession } from "../session/SessionContext";
import { PermissionDeniedState } from "../components/states";
import { AllowanceCard } from "../components/tools/AllowanceCard";
import { ProviderAccessPanel } from "../components/consumption/ProviderAccessPanel";
import styles from "./MyToolsPage.module.css";

/**
 * The "My Tools" screen: where a person manages the AI tools they hold, as distinct from "My Usage",
 * which shows what they have spent. It carries the token allowance split across providers and, for a
 * provider that issues keys such as Amazon Bedrock, a card with the key, the endpoint and the models.
 * Each card loads its own data and a provider card hides itself when it has nothing to offer, so the
 * page needs no loading state of its own.
 */
export function MyToolsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();

  // Every signed-in person can manage their own tools, as they can see their own usage.
  if (snapshot.status !== "signed-in") {
    return <PermissionDeniedState title={t("myTools.denied.title")} description={t("myTools.denied.description")} />;
  }

  return (
    <section className={styles.page} aria-labelledby="my-tools-heading">
      {/* The navigation already says where the person is, so the page name is for screen readers only. */}
      <h1 id="my-tools-heading" className="visually-hidden">
        {t("myTools.title")}
      </h1>
      <AllowanceCard />
      <ProviderAccessPanel />
    </section>
  );
}
